import * as React from "react";
import styled from "styled-components";
import { toast } from "react-toastify";

import { Button } from "src/library/components/Button";
import {
  BasicModal,
  BasicModalProps
} from "src/library/components/Modal/BasicModal";
import { ErrorNotice } from "src/core/shared/components/ErrorNotice";
import {
  usePendingCustomerApi,
  PendingCustomerStatus
} from "src/core/pending-customers/api/pendingCustomer.api";
import { Select } from "src/library/components/Forms";
import { Text } from "src/library/components/Text";

type PendingCustomersExportModalProps = Pick<BasicModalProps, "onDismiss" | "open">;

export const PendingCustomersExportModal: React.ReactFCWithChildren<PendingCustomersExportModalProps> = ({ onDismiss, open }) => {
  const [ exportResponse, exportRequest ] = usePendingCustomerApi("EXPORT_PENDING_CUSTOMERS");
  const [ selectedStatus, setSelectedStatus ] = React.useState<PendingCustomerStatus | "">(PendingCustomerStatus.Pending);

  // handle the response and open the file to trigger the download
  React.useEffect(() => {
    if (exportResponse.data) {
      toast.success(exportResponse.data.message);
      exportResponse.data = null;
      onDismiss();
    }
  }, [
    exportResponse,
    exportResponse.data,
    onDismiss
  ]);

  return (
    <StyledBasicModal
      title="Export Pending Customers"
      open={open}
      onDismiss={onDismiss}
    >
      {exportResponse.error && (
        <ErrorNotice
          message={exportResponse.error.message}
          type="error"
        />
      )}

      <Text as="p">
        Exporting pending customers will update their status to "onboarded".
      </Text>

      <Select
        as="select"
            // We default to only exporting pending customers...
            // but we can also export all pending customers if they want
        options={[
          {
            text: "All customer requests",
            value: ""
          }
        ]}
        handleChange={e => setSelectedStatus(e.target.value)}
        name="new-customer-select"
        label="New Customer Status"
        autoHideLabels
        value={selectedStatus}
        unselectedOption={{
          text: `All ${PendingCustomerStatus.Pending} customer requests`,
          value: PendingCustomerStatus.Pending
        }}
      />

      <div className="action-buttons">
        <Button
          className="submit-button"
          loading={exportResponse.pending}
          disabled={exportResponse.pending}
          onClick={() => {
            exportRequest({ filters: selectedStatus !== "" && { status: selectedStatus } || {} });
          }}
        >
          Export
        </Button>
      </div>
    </StyledBasicModal>
  );
};

const StyledBasicModal = styled(BasicModal)`
  .loading-spinner-container {
    min-height: 100px;
  }

  .action-buttons {
    margin-top: ${({ theme }) => theme.spacing.margins.md};
  }
`;
